import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Alert,
  OverlayTrigger,
  Tooltip as BSTooltip,
  Tabs,
  Tab,
  Modal,
  Toast,
  ToastContainer,
  Accordion,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/** Aligned with `src/pages/Pricing.jsx` list prices (USD / month). */
const PLAN_MONTHLY = { pro: 199.99, team: 399.99, enterprise: 1499.99 };

const INITIAL_MESSAGES = [
  {
    role: 'assistant',
    text:
      'Hi — I’m your ROI agent. Tell me about your site traffic, pipeline, and team in plain language, or adjust the form on the right. Or just say "Interview." I’ll keep the numbers in sync.',
  },
];

function formatUsd(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
}

function formatPlanMonthlyUsd(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

/** Matches Dashboard / GettingStarted: outline-info circle + Bootstrap Icons tooltip. */
function RoiHelpIcon({ text, id, placement = 'right' }) {
  return (
    <OverlayTrigger placement={placement} delay={{ show: 150, hide: 0 }} overlay={<BSTooltip id={id} className="bg-dark">{text}</BSTooltip>}>
      <button
        type="button"
        className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle"
        style={{ width: 18, height: 18, lineHeight: '16px' }}
        aria-label="Help"
      >
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true" />
      </button>
    </OverlayTrigger>
  );
}

function RoiCalculator() {
  const [monthlyVisitors, setMonthlyVisitors] = React.useState(300);
  const [monthlyMqls, setMonthlyMqls] = React.useState(3);
  const [mqlToSqlPct, setMqlToSqlPct] = React.useState(10);
  const [acv, setAcv] = React.useState(250000);
  const [salesCycleDays, setSalesCycleDays] = React.useState(120);
  const [repCount, setRepCount] = React.useState(6);
  const [annualCostPerRep, setAnnualCostPerRep] = React.useState(140000);
  const [leadMult, setLeadMult] = React.useState(2.8);
  const [qualSpeedPct, setQualSpeedPct] = React.useState(18);
  const [cycleReductionPct, setCycleReductionPct] = React.useState(12);
  const [closeLiftPct, setCloseLiftPct] = React.useState(8);
  const [hoursSavedPerWeek, setHoursSavedPerWeek] = React.useState(4);
  const [plan, setPlan] = React.useState('team');
  const [chatInput, setChatInput] = React.useState('');
  const [messages, setMessages] = React.useState(INITIAL_MESSAGES);
  const [chatLoading, setChatLoading] = React.useState(false);
  const [chatError, setChatError] = React.useState(null);
  const [offerSent, setOfferSent] = React.useState(false);
  const [capturedEmail, setCapturedEmail] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [toast, setToast] = useState({ show: false, variant: 'success', text: '' });
  const chatScrollRef = useRef(null);
  const sessionIdRef = useRef(crypto.randomUUID());

  const edgeBase = (process.env.REACT_APP_SKINNY_SUPABASE_URL || '').replace(/\/+$/, '');
  const anonKey = process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY || '';

  useEffect(() => {
    document.title = 'ROI Calculator | TheSkinnyAI';
  }, []);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const derived = useMemo(() => {
    const monthlySql = monthlyMqls * (mqlToSqlPct / 100);
    const mqlAfter = monthlyMqls * leadMult;
    const effectiveCloseLift = 1 + closeLiftPct / 100;
    const monthlySqlAfter = mqlAfter * (mqlToSqlPct / 100) * effectiveCloseLift;
    const pipelineMonthlyBefore = monthlySql * (acv / 12);
    const pipelineMonthlyAfter = monthlySqlAfter * (acv / 12);
    const pipelineLiftAnnual = (pipelineMonthlyAfter - pipelineMonthlyBefore) * 12;
    const hourly = annualCostPerRep / 2080;
    const timeSaveAnnual = repCount * hoursSavedPerWeek * 52 * hourly;
    const toolMonthly = PLAN_MONTHLY[plan] ?? PLAN_MONTHLY.team;
    const toolAnnual = toolMonthly * 12;
    const cycleEfficiency = 1 + cycleReductionPct / 200;
    const adjustedLift = pipelineLiftAnnual * cycleEfficiency * (1 + qualSpeedPct / 200);
    const netAnnual = adjustedLift + timeSaveAnnual - toolAnnual;
    const roiPct = toolAnnual > 0 ? (netAnnual / toolAnnual) * 100 : 0;
    const paybackDays =
      netAnnual > 0 && toolAnnual > 0
        ? Math.max(1, Math.round((toolAnnual / Math.max(netAnnual, 1)) * 365))
        : null;
    return {
      monthlySql,
      monthlySqlAfter,
      mqlAfter: Math.round(mqlAfter),
      pipelineMonthlyBefore,
      pipelineMonthlyAfter,
      pipelineLiftAnnual,
      timeSaveAnnual,
      toolAnnual,
      netAnnual,
      roiPct,
      paybackDays,
    };
  }, [
    monthlyMqls,
    mqlToSqlPct,
    acv,
    leadMult,
    closeLiftPct,
    repCount,
    hoursSavedPerWeek,
    annualCostPerRep,
    plan,
    cycleReductionPct,
    qualSpeedPct,
  ]);

  const barData = useMemo(
    () => ({
      labels: ['Qualified leads / mo (proxy)', 'Pipeline $ / mo', 'Rep time saved $ / mo'],
      datasets: [
        {
          label: 'Before',
          data: [
            monthlyMqls,
            derived.pipelineMonthlyBefore / 1000,
            0.001,
          ],
          backgroundColor: 'rgba(108, 117, 125, 0.65)',
          borderRadius: 6,
        },
        {
          label: 'After (with TheSkinnyAI)',
          data: [
            derived.mqlAfter,
            derived.pipelineMonthlyAfter / 1000,
            Math.max(derived.timeSaveAnnual / 12 / 1000, 0.05),
          ],
          backgroundColor: 'rgba(13, 110, 253, 0.75)',
          borderRadius: 6,
        },
      ],
    }),
    [monthlyMqls, derived]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        title: { display: false },
      },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true },
      },
    }),
    []
  );

  const getFormState = useCallback(() => ({
    monthlyVisitors, monthlyMqls, mqlToSqlPct, acv, salesCycleDays,
    repCount, annualCostPerRep, leadMult, qualSpeedPct, cycleReductionPct,
    closeLiftPct, hoursSavedPerWeek, plan,
  }), [monthlyVisitors, monthlyMqls, mqlToSqlPct, acv, salesCycleDays,
    repCount, annualCostPerRep, leadMult, qualSpeedPct, cycleReductionPct,
    closeLiftPct, hoursSavedPerWeek, plan]);

  const FIELD_SETTERS = useMemo(() => ({
    monthlyVisitors: setMonthlyVisitors,
    monthlyMqls: setMonthlyMqls,
    mqlToSqlPct: setMqlToSqlPct,
    acv: setAcv,
    salesCycleDays: setSalesCycleDays,
    repCount: setRepCount,
    annualCostPerRep: setAnnualCostPerRep,
    leadMult: setLeadMult,
    qualSpeedPct: setQualSpeedPct,
    cycleReductionPct: setCycleReductionPct,
    closeLiftPct: setCloseLiftPct,
    hoursSavedPerWeek: setHoursSavedPerWeek,
    plan: setPlan,
  }), []);

  function applyFields(fields) {
    if (!fields || typeof fields !== 'object') return;
    Object.entries(fields).forEach(([key, val]) => {
      if (val == null) return;
      const setter = FIELD_SETTERS[key];
      if (setter) setter(typeof val === 'number' ? val : val);
    });
  }

  async function handleSend(e) {
    e.preventDefault();
    const t = chatInput.trim();
    if (!t || chatLoading) return;

    const userMsg = { role: 'user', text: t };
    setMessages((m) => [...m, userMsg]);
    setChatInput('');
    setChatError(null);
    setChatLoading(true);

    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.text }));

    try {
      const res = await fetch(`${edgeBase}/functions/v1/roi-assistant`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          apikey: anonKey,
          'x-session-id': sessionIdRef.current,
        },
        body: JSON.stringify({
          message: t,
          history,
          formState: getFormState(),
        }),
      });

      if (res.status === 429) {
        setChatError('Rate limited — please wait a moment or adjust the form directly.');
        setMessages((m) => [...m, { role: 'assistant', text: "I've hit a rate limit. You can still adjust the form on the right directly — I'll be available again shortly." }]);
        return;
      }

      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        console.error('[RoiCalculator] roi-assistant error', res.status, errBody.slice(0, 300));
        setChatError('Something went wrong — try again or use the form directly.');
        setMessages((m) => [...m, { role: 'assistant', text: 'Sorry, I ran into an issue. You can adjust the form manually while I recover.' }]);
        return;
      }

      const data = await res.json();
      console.log('[RoiCalculator] AI response:', JSON.stringify(data, null, 2));
      applyFields(data.fields);
      if (data.offerSent) setOfferSent(true);

      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: data.message || 'Done.',
          highlight: !!data.offerSent && !offerSent,
        },
      ]);

      // If AI extracted an email, trigger PDF report send
      if (data.email) {
        setCapturedEmail(data.email);
        sendRoiReport(data.email);
      }
    } catch (err) {
      console.error('[RoiCalculator] fetch error', err);
      setChatError('Network error — check your connection.');
      setMessages((m) => [...m, { role: 'assistant', text: 'I couldn\'t reach the server. You can still use the form directly.' }]);
    } finally {
      setChatLoading(false);
    }
  }

  async function sendRoiReport(email, { fromButton = false } = {}) {
    if (fromButton) setEmailSending(true);
    try {
      const conversationSummary = 'Thanks for using the TheSkinnyAI ROI Calculator. Below is your personalized projection showing how AI-powered sales engagement could impact your pipeline and team efficiency.';

      const res = await fetch(`${edgeBase}/functions/v1/generate-roi-pdf`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          apikey: anonKey,
          'x-session-id': sessionIdRef.current,
        },
        body: JSON.stringify({
          email,
          inputs: getFormState(),
          results: derived,
          conversationSummary,
          chatHistory: messages.map((m) => ({ role: m.role, text: m.text })),
          marketingOptIn: false,
          source: 'direct',
        }),
      });

      if (res.ok) {
        setCapturedEmail(email);
        if (fromButton) {
          setToast({ show: true, variant: 'success', text: `Results sent to ${email}. Check your inbox!` });
        } else {
          setMessages((m) => [...m, { role: 'assistant', text: `Done! I've sent the summary and calculations to ${email}. Check your inbox (and spam folder) in the next minute or two.` }]);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        if (res.status === 429) {
          const msg = 'Email send limit reached. Please try again in a few minutes.';
          fromButton ? setToast({ show: true, variant: 'warning', text: msg }) : setMessages((m) => [...m, { role: 'assistant', text: msg }]);
        } else if (errData.error === 'invalid_email') {
          const msg = 'That email address doesn\'t look right. Please check and try again.';
          fromButton ? setToast({ show: true, variant: 'danger', text: msg }) : setMessages((m) => [...m, { role: 'assistant', text: msg }]);
        } else {
          const msg = 'Couldn\'t send the email right now. Please try again shortly.';
          fromButton ? setToast({ show: true, variant: 'danger', text: msg }) : setMessages((m) => [...m, { role: 'assistant', text: msg }]);
        }
      }
    } catch (err) {
      console.error('[RoiCalculator] sendRoiReport error', err);
      const msg = 'Something went wrong sending the email. Please try again.';
      fromButton ? setToast({ show: true, variant: 'danger', text: msg }) : setMessages((m) => [...m, { role: 'assistant', text: msg }]);
    } finally {
      if (fromButton) setEmailSending(false);
    }
  }

  function handleEmailButtonClick() {
    if (capturedEmail) {
      sendRoiReport(capturedEmail, { fromButton: true });
    } else {
      setEmailInput('');
      setShowEmailModal(true);
    }
  }

  function handleEmailModalSubmit(e) {
    e.preventDefault();
    const addr = emailInput.trim().toLowerCase();
    if (!addr || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      setToast({ show: true, variant: 'danger', text: 'Please enter a valid email address.' });
      return;
    }
    setShowEmailModal(false);
    sendRoiReport(addr, { fromButton: true });
  }

  return (
    <main className="roi-calculator-page bg-light pb-5">
      <style>{`
        .roi-calculator-page .roi-brand-strip {
          background: linear-gradient(90deg, #0d6efd 0%, #0a58ca 100%);
          color: #fff;
          font-size: 0.875rem;
        }
        .roi-calculator-page .roi-chat-scroll {
          max-height: min(62vh, 520px);
          overflow-y: auto;
        }
        .roi-calculator-page .bubble-user {
          background: #e7f1ff;
          border: 1px solid #b6d4fe;
        }
        .roi-calculator-page .bubble-assistant {
          background: #fff;
          border: 1px solid #dee2e6;
        }
        .roi-calculator-page .bubble-offer {
          border-color: #0d6efd;
          background: #f0f7ff;
        }
        .roi-calculator-page .roi-sticky-col {
          position: sticky;
          top: 5.5rem;
        }
        .roi-calculator-page .roi-model-tabs .nav-link {
          padding: 0.4rem 0.5rem;
          font-size: 0.8125rem;
        }
        @media (max-width: 991.98px) {
          .roi-calculator-page .roi-sticky-col { position: static; top: auto; }
          .roi-calculator-page .roi-chat-scroll { max-height: 360px; }
        }
      `}</style>

      <div className="roi-brand-strip py-2 border-bottom">
        <Container fluid="lg" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-semibold">TheSkinnyAI</span>
            <span className="opacity-75 d-none d-sm-inline">|</span>
            <span className="opacity-90 d-none d-sm-inline">ROI Calculator</span>
          </div>
          <div>
            <span className="opacity-90">Powered by TheSkinnyAI</span>
            <a className="text-white ms-2 fw-semibold" href="/home.html">
              theskinnyai.com
            </a>
          </div>
        </Container>
      </div>

      <Container fluid="lg" className="pt-4">
        <header className="mb-4">
          <h1 className="h2 fw-bold mb-1">AI Sales Agent ROI Calculator</h1>
          <p className="text-muted mb-0 col-lg-8">
            Calculate the return on investment of adding an AI sales agent to your pipeline. Model how AI-driven lead qualification, faster sales cycles, and automated outreach translate into revenue lift and rep productivity gains for your team.
          </p>
        </header>

        <Row className="g-4">
          <Col lg={7}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="bg-white py-3 border-bottom">
                <span className="fw-semibold">ROI Agent</span>
              </Card.Header>
              <Card.Body className="d-flex flex-column p-0">
                <div ref={chatScrollRef} className="roi-chat-scroll p-3 flex-grow-1">
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      className={`mb-3 rounded-3 p-3 small ${
                        msg.role === 'user'
                          ? 'bubble-user ms-md-4'
                          : msg.highlight
                            ? 'bubble-assistant bubble-offer me-md-4'
                            : 'bubble-assistant me-md-4'
                      }`}
                    >
                      <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}>
                        {msg.role === 'user' ? 'You' : 'ROI Agent'}
                      </div>
                      <div className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-top bg-light p-3">
                  {chatError && (
                    <Alert variant="warning" dismissible onClose={() => setChatError(null)} className="py-1 px-2 small mb-2">
                      {chatError}
                    </Alert>
                  )}
                  <Form onSubmit={handleSend}>
                    <InputGroup>
                      <Form.Control
                        placeholder="Describe your funnel, team, or goals…"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        aria-label="Chat message"
                        disabled={chatLoading}
                      />
                      <Button type="submit" variant="primary" disabled={chatLoading}>
                        {chatLoading ? '…' : 'Send'}
                      </Button>
                    </InputGroup>
                  </Form>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <div className="roi-sticky-col">
              <Card className="shadow-sm border-0">
                <Card.Body className="p-0 small">
                  <Tabs
                    defaultActiveKey="inputs"
                    id="roi-model-tabs"
                    fill
                    justify
                    variant="tabs"
                    className="roi-model-tabs px-2 pt-2 border-bottom bg-white"
                  >
                    <Tab eventKey="inputs" title="Inputs" unmountOnExit={false}>
                      <div className="p-3 pt-2">
                  <Row className="g-2 mb-2">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Monthly visitors</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={monthlyVisitors}
                          onChange={(e) => setMonthlyVisitors(Number(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="d-flex align-items-center">
                          Monthly MQLs
                          <RoiHelpIcon
                            id="roi-tip-mqls"
                            text="Marketing qualified leads per month: visitors your team agrees are worth follow-up before an opportunity is formally opened."
                          />
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={monthlyMqls}
                          onChange={(e) => setMonthlyMqls(Number(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-2">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="d-flex align-items-center">
                          MQL → SQL %
                          <RoiHelpIcon
                            id="roi-tip-mql-sql"
                            text="Percentage of MQLs that become SQLs (sales-qualified opportunities your team accepts as real pipeline)."
                          />
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          value={mqlToSqlPct}
                          onChange={(e) => setMqlToSqlPct(Number(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="d-flex align-items-center">
                          ACV ($)
                          <RoiHelpIcon
                            id="roi-tip-acv"
                            text="Average annual contract value: typical yearly revenue from one closed customer (or one subscription year) in this segment."
                          />
                        </Form.Label>
                        <Form.Control type="number" min={0} value={acv} onChange={(e) => setAcv(Number(e.target.value))} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-2">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Sales cycle (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          value={salesCycleDays}
                          onChange={(e) => setSalesCycleDays(Number(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Reps</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={repCount}
                          onChange={(e) => setRepCount(Number(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      Fully loaded $ / rep / year
                      <RoiHelpIcon
                        id="roi-tip-fl-rep"
                        text="Total employer cost per rep for a year — base, variable comp, benefits, tools, and overhead — used to value time saved in the model."
                      />
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={annualCostPerRep}
                      onChange={(e) => setAnnualCostPerRep(Number(e.target.value))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label>Plan ($ / mo)</Form.Label>
                    <Form.Select value={plan} onChange={(e) => setPlan(e.target.value)}>
                      <option value="pro">Pro — {formatPlanMonthlyUsd(PLAN_MONTHLY.pro)}/mo</option>
                      <option value="team">Team — {formatPlanMonthlyUsd(PLAN_MONTHLY.team)}/mo</option>
                      <option value="enterprise">Enterprise — {formatPlanMonthlyUsd(PLAN_MONTHLY.enterprise)}/mo</option>
                    </Form.Select>
                  </Form.Group>
                      </div>
                    </Tab>
                    <Tab eventKey="assumptions" title="Assumptions" unmountOnExit={false}>
                      <div className="p-3 pt-2">
                  <div className="d-flex align-items-center text-muted text-uppercase fw-semibold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>
                    <span className="me-1">TheSkinnyAI assumptions</span>
                    <RoiHelpIcon
                      id="roi-tip-assumptions-section"
                      text="Sliders represent how TheSkinnyAI could improve your funnel and rep workload versus your baseline. Adjust them to match your expectations or leave the defaults."
                    />
                  </div>
                  <Form.Group className="mb-2">
                    <Form.Label className="d-flex align-items-center">
                      Lead volume multiplier ({leadMult.toFixed(1)}×)
                      <RoiHelpIcon
                        id="roi-tip-lead-mult"
                        text="Expected lift in qualified web or chat-sourced leads once an AI assistant engages visitors (spec default 2.8×)."
                      />
                    </Form.Label>
                    <Form.Range min={1} max={4} step={0.1} value={leadMult} onChange={(e) => setLeadMult(Number(e.target.value))} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="d-flex align-items-center">
                      Qualification speed + ({qualSpeedPct}%)
                      <RoiHelpIcon
                        id="roi-tip-qual-speed"
                        text="How much faster leads move from first touch to sales-ready status — less back-and-forth and clearer buyer intent."
                      />
                    </Form.Label>
                    <Form.Range min={0} max={50} value={qualSpeedPct} onChange={(e) => setQualSpeedPct(Number(e.target.value))} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="d-flex align-items-center">
                      Sales cycle reduction ({cycleReductionPct}%)
                      <RoiHelpIcon
                        id="roi-tip-cycle-red"
                        text="Expected shortening of average days from SQL to close when better qualification happens earlier in the journey."
                      />
                    </Form.Label>
                    <Form.Range
                      min={0}
                      max={40}
                      value={cycleReductionPct}
                      onChange={(e) => setCycleReductionPct(Number(e.target.value))}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="d-flex align-items-center">
                      Close rate lift ({closeLiftPct}%)
                      <RoiHelpIcon
                        id="roi-tip-close-lift"
                        text="Expected increase in win rate when opportunities enter the funnel better informed and pre-qualified."
                      />
                    </Form.Label>
                    <Form.Range min={0} max={30} value={closeLiftPct} onChange={(e) => setCloseLiftPct(Number(e.target.value))} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      Hours saved / rep / week ({hoursSavedPerWeek}h)
                      <RoiHelpIcon
                        id="roi-tip-hours-saved"
                        text="Rep time reclaimed when the assistant handles repetitive questions, scheduling, and triage instead of the sales team."
                      />
                    </Form.Label>
                    <Form.Range min={0} max={15} value={hoursSavedPerWeek} onChange={(e) => setHoursSavedPerWeek(Number(e.target.value))} />
                  </Form.Group>

                      </div>
                    </Tab>
                    <Tab eventKey="results" title="Results" unmountOnExit={false}>
                      <div className="p-3 pt-2">
                <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom">
                  <span className="fw-semibold text-muted">Your projected results</span>
                  <RoiHelpIcon
                    id="roi-tip-results-header"
                    placement="left"
                    text="These results update in real time from your inputs. MQLs are multiplied by the lead volume factor; SQLs apply conversion and close-rate lift; pipeline lift is annualized; time savings use hours × reps × loaded hourly cost; tool cost is plan × 12; net and ROI combine those components."
                  />
                </div>
                  <Row className="g-2 mb-3">
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            Visitors / mo
                          </div>
                          <RoiHelpIcon
                            id="roi-res-visitors"
                            placement="top"
                            text="Monthly site visitors from your model inputs. Shown for context alongside funnel metrics."
                          />
                        </div>
                        <div className="fw-bold">{monthlyVisitors.toLocaleString()}</div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            MQLs / mo (after ×)
                          </div>
                          <RoiHelpIcon
                            id="roi-res-mql"
                            placement="top"
                            text="Before = your current monthly MQLs; after = MQLs × lead volume multiplier (projected with TheSkinnyAI)."
                          />
                        </div>
                        <div className="fw-bold">
                          {monthlyMqls.toLocaleString()} → {derived.mqlAfter.toLocaleString()}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            SQLs / mo →
                          </div>
                          <RoiHelpIcon
                            id="roi-res-sql"
                            placement="top"
                            text="Estimated SQLs per month before vs after applying MQL volume, conversion to SQL, and close-rate lift."
                          />
                        </div>
                        <div className="fw-bold">
                          {derived.monthlySql.toFixed(1)} → {derived.monthlySqlAfter.toFixed(1)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            Pipeline lift / yr
                          </div>
                          <RoiHelpIcon
                            id="roi-res-pipeline"
                            placement="top"
                            text="Annualized difference in pipeline dollars, calculated from ACV spread across monthly SQLs before and after TheSkinnyAI."
                          />
                        </div>
                        <div className="fw-bold text-primary">{formatUsd(derived.pipelineLiftAnnual)}</div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            Time save / yr
                          </div>
                          <RoiHelpIcon
                            id="roi-res-time"
                            placement="top"
                            text="Dollar value of rep hours saved: hours per rep per week × number of reps × 52 × (fully loaded annual cost ÷ 2080 work hours)."
                          />
                        </div>
                        <div className="fw-bold">{formatUsd(derived.timeSaveAnnual)}</div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            Tool cost / yr
                          </div>
                          <RoiHelpIcon
                            id="roi-res-tool"
                            placement="top"
                            text="Annual subscription cost for the selected plan (monthly price × 12)."
                          />
                        </div>
                        <div className="fw-bold">{formatUsd(derived.toolAnnual)}</div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            Net / yr
                          </div>
                          <RoiHelpIcon
                            id="roi-res-net"
                            placement="top"
                            text="Net annual benefit: adjusted pipeline lift plus annualized time savings minus annual tool cost."
                          />
                        </div>
                        <div className={`fw-bold ${derived.netAnnual >= 0 ? 'text-success' : 'text-danger'}`}>
                          {formatUsd(derived.netAnnual)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="p-2 bg-white rounded border h-100">
                        <div className="d-flex align-items-start justify-content-between gap-1">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                            ROI % (yr 1)
                          </div>
                          <RoiHelpIcon
                            id="roi-res-roi"
                            placement="top"
                            text="Return on investment for year one: (net annual benefit ÷ annual tool cost) × 100. Not financial advice; for discussion with your team."
                          />
                        </div>
                        <div className="fw-bold">{derived.roiPct.toFixed(0)}%</div>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div className="p-2 bg-primary bg-opacity-10 rounded border border-primary border-opacity-25 text-center">
                        <span className="fw-bold text-primary">
                          {derived.paybackDays
                            ? `Breaks even in ${(derived.paybackDays / 30.44).toFixed(1)} months`
                            : 'Adjust inputs to see payback period'}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="text-muted small">Before vs after</span>
                    <RoiHelpIcon
                      id="roi-res-chart"
                      placement="top"
                      text="Bars compare before vs after on scaled units (MQL count, pipeline $ in thousands, time savings in thousands). Axis scaling is for visual comparison."
                    />
                  </div>
                  <div style={{ height: 260 }}>
                    <Bar data={barData} options={barOptions} />
                  </div>
                  <div className="d-grid gap-2">
                    <Button variant="outline-primary" onClick={handleEmailButtonClick} disabled={emailSending}>
                      {emailSending ? 'Sending…' : 'Email me my results'}
                    </Button>
                    <Button variant="primary" as={Link} to="/contact">
                      Book a demo
                    </Button>
                  </div>
                  <Accordion flush className="mt-3">
                    <Accordion.Item eventKey="0" className="border-0">
                      <Accordion.Header className="p-0">
                        <small className="text-muted">Methodology &amp; data sources</small>
                      </Accordion.Header>
                      <Accordion.Body className="px-0 pt-2 pb-0" style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                        <p className="mb-2">
                          This calculator uses a simplified funnel model grounded in published SaaS benchmarks:
                        </p>
                        <ul className="mb-2 ps-3">
                          <li><strong>Lead volume multiplier (2.8×)</strong> — AI chatbots increase qualified lead capture 2–4× vs. static forms (<a href="/insights/ai-chatbot-pricing-comparison-2026.html">AI Chatbot Pricing Comparison 2026</a>).</li>
                          <li><strong>Close-rate lift (8%)</strong> — Faster qualification correlates with 5–15% win-rate improvement (<a href="https://nucleusresearch.com/" target="_blank" rel="noopener noreferrer">Nucleus Research</a> ROI studies).</li>
                          <li><strong>Cycle reduction (12%)</strong> — Automated nurture and instant response shorten B2B cycles by 10–20% (<a href="https://openviewpartners.com/" target="_blank" rel="noopener noreferrer">OpenView</a> SaaS Benchmarks).</li>
                          <li><strong>Rep time saved (4 hrs/wk)</strong> — Sales reps spend 21% of time on manual data entry and lead research; AI offloads 3–6 hrs/wk (<a href="https://www.salesforce.com/resources/research-reports/state-of-sales/" target="_blank" rel="noopener noreferrer">Salesforce State of Sales</a>).</li>
                          <li><strong>Qualification speed (18%)</strong> — Instant AI response reduces time-to-qualify by 15–25% vs. 24–48 hr human SLA (<a href="https://hbr.org/2011/03/the-short-life-of-online-sales-leads" target="_blank" rel="noopener noreferrer">HBR</a>).</li>
                        </ul>
                        <p className="text-muted mb-0">
                          Actual results vary by industry, traffic quality, and sales motion. Adjust assumptions to reflect your business. Not financial advice.
                        </p>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                      </div>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h6">Email your results</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEmailModalSubmit}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="you@company.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                autoFocus
                required
              />
              <Form.Text className="text-muted">
                We'll send your ROI model to this address.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit">Send results</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={toast.show}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
          bg={toast.variant}
          delay={5000}
          autohide
        >
          <Toast.Body className={toast.variant === 'success' || toast.variant === 'danger' ? 'text-white' : ''}>
            {toast.text}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </main>
  );
}

export default RoiCalculator;
